import { supabase } from './supabaseClient';
import { CRM_ACCOUNT_TYPES, isLegacyCrmOwnerEmail } from './crmAccountTypes';
import {
  assertCrmSignupSchemaReady,
  formatSupabaseError,
  getCrmDbCapabilities,
} from './crmDbSchema';

export const APP_ACCOUNT_KIND = {
  DASHBOARD: 'dashboard',
  CRM: 'crm',
};

export const ACCOUNT_SETUP_COMPLETE_EVENT = 'tradelynx-account-setup-complete';

const PENDING_SIGNUP_STORAGE_KEY = 'tradelynx_pending_signup_options';

/** Set during signUp() so auth callbacks can finish CRM setup before the app loads. */
let pendingSignupAccountOptions = null;

export function setPendingSignupAccountOptions(options) {
  pendingSignupAccountOptions = options || null;
  if (typeof window === 'undefined') return;
  try {
    if (options) {
      sessionStorage.setItem(PENDING_SIGNUP_STORAGE_KEY, JSON.stringify(options));
    } else {
      sessionStorage.removeItem(PENDING_SIGNUP_STORAGE_KEY);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

function getStoredPendingSignupOptions() {
  if (pendingSignupAccountOptions) return pendingSignupAccountOptions;
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PENDING_SIGNUP_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearPendingSignupAccountOptions() {
  setPendingSignupAccountOptions(null);
}

function notifyAccountSetupComplete() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ACCOUNT_SETUP_COMPLETE_EVENT));
  }
}

async function userHasCrmTeamMembership(userId, emailLower, caps) {
  if (!caps?.teamMembersCrmRole) return false;

  const { data: byUser, error: byUserErr } = await supabase
    .from('team_members')
    .select('id')
    .eq('user_id', userId)
    .not('crm_role', 'is', null)
    .in('status', ['accepted', 'pending'])
    .limit(1)
    .maybeSingle();
  if (byUserErr) return false;
  if (byUser) return true;

  if (!emailLower) return false;
  const { data: byEmail, error: byEmailErr } = await supabase
    .from('team_members')
    .select('id')
    .eq('email', emailLower)
    .not('crm_role', 'is', null)
    .in('status', ['accepted', 'pending'])
    .limit(1)
    .maybeSingle();
  if (byEmailErr) return false;
  return Boolean(byEmail);
}

async function findPendingCrmInviteByEmail(emailLower, caps) {
  if (!emailLower || !caps?.teamMembersCrmRole) return null;
  let { data, error } = await supabase
    .from('team_members')
    .select('id, owner_id, crm_role, status, user_id')
    .eq('email', emailLower)
    .not('crm_role', 'is', null)
    .in('status', ['accepted', 'pending'])
    .order('invited_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    ({ data, error } = await supabase
      .from('team_members')
      .select('id, owner_id, crm_role, status, user_id')
      .eq('email', emailLower)
      .not('crm_role', 'is', null)
      .in('status', ['accepted', 'pending'])
      .limit(1)
      .maybeSingle());
  }
  if (error) {
    console.error('[accountSetup] pending CRM invite lookup failed:', error);
    return null;
  }
  return data;
}

async function readProfileSetupState(userId, caps) {
  if (caps.profilesAccountSetupComplete) {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, account_setup_complete, role')
      .eq('id', userId)
      .maybeSingle();
    if (!error) {
      return {
        profileSetupDone: Boolean(data?.account_setup_complete),
        role: data?.role,
      };
    }
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('id, role')
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    return { profileSetupDone: false, role: null };
  }
  return { profileSetupDone: false, role: data?.role };
}

async function upsertProfileSafe(row) {
  const { error } = await supabase.from('profiles').upsert(row, { onConflict: 'id' });
  if (!error) return;
  const fallback = { ...row };
  delete fallback.account_setup_complete;
  delete fallback.crm_account_type;
  const { error: retryErr } = await supabase.from('profiles').upsert(fallback, { onConflict: 'id' });
  if (retryErr) throw retryErr;
}

async function bossIsCrmWorkspace(boss, caps) {
  if (!boss?.id) return false;
  if (boss.crm_account_type === CRM_ACCOUNT_TYPES.FULL || isLegacyCrmOwnerEmail(boss.email)) {
    return true;
  }
  if (!caps?.teamMembersCrmRole) {
    return isLegacyCrmOwnerEmail(boss.email);
  }
  const { data, error } = await supabase
    .from('team_members')
    .select('id')
    .eq('owner_id', boss.id)
    .not('crm_role', 'is', null)
    .limit(1)
    .maybeSingle();
  if (error) return isLegacyCrmOwnerEmail(boss.email);
  return Boolean(data);
}

async function findCrmOwnerByJoinCode(joinCode, caps) {
  const code = String(joinCode || '').trim();
  if (code.length !== 6) {
    throw new Error('Enter the 6-digit CRM team code from your admin.');
  }
  assertCrmSignupSchemaReady(caps);

  let boss = null;
  let res = await supabase
    .from('profiles')
    .select(
      caps.profilesCrmAccountType
        ? 'id, email, crm_account_type, join_code'
        : 'id, email, join_code',
    )
    .eq('join_code', code)
    .maybeSingle();
  if (res.error) throw new Error(formatSupabaseError(res.error));
  boss = res.data;
  if (!boss) throw new Error('No CRM team found with that code. Check the code and try again.');

  if (!(await bossIsCrmWorkspace(boss, caps))) {
    throw new Error('That team code is not linked to a CRM workspace. Ask your admin for the CRM code.');
  }

  return boss;
}

function normalizeSignupCrmRole(crmRole) {
  const v = String(crmRole || '').trim().toLowerCase();
  if (v === CRM_ACCOUNT_TYPES.SETTER || v === CRM_ACCOUNT_TYPES.CLOSER) return v;
  return null;
}

async function linkUserToCrmTeam({
  userId,
  bossId,
  emailLower,
  displayName,
  crmRole,
  caps,
}) {
  assertCrmSignupSchemaReady(caps);
  const role = normalizeSignupCrmRole(crmRole);
  if (!role) {
    throw new Error('Choose Setter or Closer on the sign-up form before creating a CRM account.');
  }
  crmRole = role;

  const { data: existingTeam } = await supabase
    .from('team_members')
    .select('id, crm_role')
    .eq('owner_id', bossId)
    .eq('user_id', userId)
    .maybeSingle();

  const { data: existingByEmail } = await supabase
    .from('team_members')
    .select('id, crm_role')
    .eq('owner_id', bossId)
    .eq('email', emailLower)
    .maybeSingle();

  const teamPatch = {
    user_id: userId,
    crm_role: crmRole,
    status: 'accepted',
    joined_at: new Date().toISOString(),
    name: displayName,
    email: emailLower,
    role: 'technician',
  };

  if (existingByEmail) {
    const { error: updErr } = await supabase
      .from('team_members')
      .update(teamPatch)
      .eq('id', existingByEmail.id);
    if (updErr) throw new Error(formatSupabaseError(updErr));
  } else if (existingTeam) {
    const { error: updErr } = await supabase
      .from('team_members')
      .update(teamPatch)
      .eq('id', existingTeam.id);
    if (updErr) throw new Error(formatSupabaseError(updErr));
  } else {
    const { error: teamErr } = await supabase.from('team_members').insert({
      owner_id: bossId,
      ...teamPatch,
    });
    if (teamErr) throw new Error(formatSupabaseError(teamErr));
  }

  const profileRow = {
    id: userId,
    email: emailLower,
    name: displayName,
    role: 'technician',
  };
  if (caps.profilesCrmAccountType) profileRow.crm_account_type = null;
  if (caps.profilesAccountSetupComplete) profileRow.account_setup_complete = true;
  await upsertProfileSafe(profileRow);
  clearPendingSignupAccountOptions();
  notifyAccountSetupComplete();
}

async function resolveAuthMetadata(user) {
  await supabase.auth.refreshSession().catch(() => {});
  const { data: refreshed, error } = await supabase.auth.getUser();
  if (!error && refreshed?.user?.id === user.id) {
    return refreshed.user.user_metadata || {};
  }
  return user?.user_metadata || {};
}

function resolveCrmSignupFields(meta, pending) {
  const stored = pending || getStoredPendingSignupOptions();
  const accountKind = meta.app_account_type || stored?.accountKind;
  const crmRole = meta.crm_role || stored?.crmRole;
  const crmJoinCode = meta.crm_join_code || stored?.crmJoinCode;
  return { accountKind, crmRole, crmJoinCode };
}

/**
 * Runs after sign-up when a session exists. Idempotent enough for retry on login.
 */
export async function completeNewAccountSetup(user, options = {}, { force = false } = {}) {
  if (!user?.id) return;

  const caps = await getCrmDbCapabilities();
  const storedSignup = getStoredPendingSignupOptions();
  let accountKind = options.accountKind || APP_ACCOUNT_KIND.DASHBOARD;
  if (
    accountKind === APP_ACCOUNT_KIND.DASHBOARD
    && storedSignup?.accountKind === APP_ACCOUNT_KIND.CRM
  ) {
    accountKind = APP_ACCOUNT_KIND.CRM;
    options = {
      ...options,
      accountKind: APP_ACCOUNT_KIND.CRM,
      crmRole: options.crmRole || storedSignup.crmRole,
      crmJoinCode: options.crmJoinCode || storedSignup.crmJoinCode,
    };
  }
  const metaKind = user.user_metadata?.app_account_type;
  if (accountKind === APP_ACCOUNT_KIND.DASHBOARD && metaKind === APP_ACCOUNT_KIND.CRM) {
    accountKind = APP_ACCOUNT_KIND.CRM;
    options = {
      ...options,
      accountKind: APP_ACCOUNT_KIND.CRM,
      crmRole: options.crmRole || user.user_metadata?.crm_role || storedSignup?.crmRole,
      crmJoinCode: options.crmJoinCode || user.user_metadata?.crm_join_code || storedSignup?.crmJoinCode,
    };
  }
  const emailLower = (user.email || options.email || storedSignup?.email || '').toLowerCase().trim();
  const displayName = user.user_metadata?.name || emailLower.split('@')[0] || 'User';

  const pendingInvite = await findPendingCrmInviteByEmail(emailLower, caps);
  if (accountKind === APP_ACCOUNT_KIND.DASHBOARD && pendingInvite?.crm_role) {
    accountKind = APP_ACCOUNT_KIND.CRM;
    options = {
      ...options,
      accountKind: APP_ACCOUNT_KIND.CRM,
      crmRole: options.crmRole || pendingInvite.crm_role,
    };
  }

  const { profileSetupDone: existingSetupDone } = await readProfileSetupState(user.id, caps);

  if (!force && existingSetupDone) {
    if (accountKind === APP_ACCOUNT_KIND.CRM) {
      const hasCrmTeam = await userHasCrmTeamMembership(user.id, emailLower, caps);
      if (hasCrmTeam) return;
    } else {
      return;
    }
  }

  if (accountKind === APP_ACCOUNT_KIND.DASHBOARD) {
    const hasCrmTeam = await userHasCrmTeamMembership(user.id, emailLower, caps);
    if (hasCrmTeam) return;

    const profileRow = {
      id: user.id,
      email: emailLower,
      name: displayName,
      role: 'owner',
    };
    if (caps.profilesCrmAccountType) profileRow.crm_account_type = null;
    if (caps.profilesAccountSetupComplete) profileRow.account_setup_complete = true;
    await upsertProfileSafe(profileRow);
    clearPendingSignupAccountOptions();
    notifyAccountSetupComplete();
    return;
  }

  if (accountKind === APP_ACCOUNT_KIND.CRM) {
    assertCrmSignupSchemaReady(caps);
    const crmRole = options.crmRole || pendingInvite?.crm_role;

    if (pendingInvite && !options.crmJoinCode) {
      await linkUserToCrmTeam({
        userId: user.id,
        bossId: pendingInvite.owner_id,
        emailLower,
        displayName,
        crmRole,
        caps,
      });
      return;
    }

    const boss = await findCrmOwnerByJoinCode(options.crmJoinCode, caps);
    await linkUserToCrmTeam({
      userId: user.id,
      bossId: boss.id,
      emailLower,
      displayName,
      crmRole,
      caps,
    });
  }
}

/** Finish setup from auth metadata after email confirmation + first login. */
export async function completeSetupFromUserMetadata(user) {
  if (!user?.id) return;

  const caps = await getCrmDbCapabilities();
  const meta = await resolveAuthMetadata(user);
  const pending = getStoredPendingSignupOptions();
  const emailLower = (user.email || pending?.email || '').toLowerCase().trim();
  const { accountKind, crmRole, crmJoinCode } = resolveCrmSignupFields(meta, pending);
  const effectiveCrmRole = crmRole || pending?.crmRole;
  const wantsCrm = accountKind === APP_ACCOUNT_KIND.CRM;

  const hasCrmTeam = await userHasCrmTeamMembership(user.id, emailLower, caps);
  const pendingInvite = !hasCrmTeam ? await findPendingCrmInviteByEmail(emailLower, caps) : null;

  const { profileSetupDone } = await readProfileSetupState(user.id, caps);
  const shouldBeCrm = wantsCrm || Boolean(pendingInvite?.crm_role);

  const runCrmSetup = async (force) => {
    const roleForSetup = effectiveCrmRole || pendingInvite?.crm_role;
    if (!normalizeSignupCrmRole(roleForSetup)) {
      throw new Error('Choose Setter or Closer on the sign-up form before creating a CRM account.');
    }
    await completeNewAccountSetup(
      user,
      {
        accountKind: APP_ACCOUNT_KIND.CRM,
        crmRole: roleForSetup,
        crmJoinCode,
        email: user.email,
      },
      { force },
    );
    await supabase.auth.updateUser({ data: { ...meta, account_setup_complete: true } }).catch(() => {});
  };

  if (shouldBeCrm && !hasCrmTeam && (crmJoinCode || pendingInvite || effectiveCrmRole)) {
    try {
      await runCrmSetup(Boolean(profileSetupDone));
      return;
    } catch (err) {
      console.error('[accountSetup] CRM setup/repair failed:', err);
      throw err;
    }
  }

  if (profileSetupDone) {
    if (accountKind && !meta.account_setup_complete) {
      supabase.auth.updateUser({ data: { ...meta, account_setup_complete: true } }).catch(() => {});
    }
    return;
  }

  if (!accountKind || meta.account_setup_complete) return;

  try {
    await completeNewAccountSetup(user, {
      accountKind,
      crmRole: effectiveCrmRole,
      crmJoinCode,
      email: user.email,
    });
    await supabase.auth.updateUser({ data: { ...meta, account_setup_complete: true } }).catch(() => {});
  } catch (err) {
    console.error('[accountSetup] deferred setup failed:', err);
    throw err;
  }
}
